import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb, CardBody } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import ProgressBar from "react-bootstrap/ProgressBar";
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  UploadOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Typography,
  Space,
  message,
  Popconfirm,
  Upload,
  Switch,
  Divider,
  Transfer,
  Row,
  Col,
  Card,
  Image,
  Collapse,
  Progress,
  Tag,
  Empty,
} from "antd";
import styles from "@/assets/scss/Tables.module.scss";
import tasks_styles from "@/assets/scss/TasksList.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { editMyTask, deleteMyTask, myTasks, taskUsers } from "@/store/tasksApi/tasksThunk";
import dayjs from "dayjs";
import "dayjs/locale/uz-latn";
dayjs.locale("uz-latn");

const { TextArea } = Input;
const { Title, Text } = Typography;

const { Panel } = Collapse;

const MyTasksList = () => {
  const dispatch = useDispatch();
  const { myTasksList, users = [] } = useSelector((state) => state.tasksApi);

  useEffect(() => {
    dispatch(taskUsers());
    dispatch(myTasks());
  }, [dispatch]);

  // Handle paginated response (new format) or array (old format)
  const myTasksArray = Array.isArray(myTasksList)
    ? myTasksList
    : (myTasksList?.items || []);

  const userOptions = useMemo(
    () =>
      (users || []).map((u) => ({
        id: u.id,
        label: `${u.surname} ${u.first_name} ${u.patronymic}`,
        picture: u.picture,
      })),
    [users]
  );

  const transferData = useMemo(
    () =>
      userOptions.map((u) => ({
        key: String(u.id),
        title: u.label,
      })),
    [userOptions]
  );

  const [searchMy, setSearchMy] = useState("");
  const [statusFilterMy, setStatusFilterMy] = useState("");

  const statusMap = {
    pending: { label: "Kutilmoqda", className: "badge bg-secondary" },
    in_progress: { label: "Jarayonda", className: "badge bg-warning text-dark" },
    submitted: { label: "Tasdiqlashda", className: "badge bg-info text-dark" },
    pending_after_rejection: { label: "Rad etilgan", className: "badge bg-danger" },
    approved: { label: "Tasdiqlangan", className: "badge bg-success" },
    rejected: { label: "Qaytarilgan", className: "badge bg-danger" },
  };

  const filteredMyTasks = myTasksArray
    .filter((item) => (statusFilterMy ? item.status === statusFilterMy : true))
    .filter((item) => (searchMy ? item.title.toLowerCase().includes(searchMy.toLowerCase()) : true));

  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form] = Form.useForm();

  const [imageFile, setImageFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const beforeUpload = () => false;

  const [assignedKeys, setAssignedKeys] = useState([]);

  const openEdit = (task) => {
    setSelectedTask(task);

    const initialSteps = Array.isArray(task?.steps)
      ? task.steps.map((s, idx) => ({
        title: s.title,
        order: s.order ?? idx,
        id: s.id,
      }))
      : [];

    setAssignedKeys((task?.assigned_users || []).map((u) => String(u.id)));
    setImageFile(null);
    setDocFile(null);

    setEditOpen(true);
    setTimeout(() => {
      form.setFieldsValue({
        title: task?.title || "",
        description: task?.description || "",
        is_approval_required: true,
        approver_id: task?.approver_id ?? task?.approver ?? undefined,
        deadline: task?.deadline ? dayjs(task.deadline) : null,
        steps_required: Boolean(task?.steps_required),
        steps: initialSteps,
      });
    }, 0);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedTask(null);
    form.resetFields();
    setImageFile(null);
    setDocFile(null);
    setAssignedKeys([]);
  };

  const safeErrText = (err) => {
    if (typeof err === "string") return err;
    if (err?.detail && typeof err.detail === "string") return err.detail;
    if (err?.message) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return "Xatolik yuz berdi.";
    }
  };

  const normalizeSteps = (rawSteps) => {
    const list = Array.isArray(rawSteps) ? rawSteps : [];
    return list
      .map((s, idx) => {
        if (!s || typeof s !== "object" || Array.isArray(s)) return null;
        const title = (s.title ?? "").trim();
        const orderNum = Number(s.order);
        const order = Number.isFinite(orderNum) ? orderNum : idx;
        if (!title) return null;
        const out = { title, order };
        if (s.id !== undefined && s.id !== null && s.id !== "") out.id = s.id;
        return out;
      })
      .filter(Boolean);
  };


  const handleEditFinish = async (values) => {
    if (!selectedTask) return;
    try {
      setEditSubmitting(true);

      const fd = new FormData();
      fd.append("title", values.title ?? selectedTask?.title ?? "");
      fd.append("description", values.description ?? selectedTask?.description ?? "");
      fd.append("is_approval_required", "true");
      fd.append("approver_id", values.approver_id ?? selectedTask?.approver_id ?? "");
      fd.append(
        "deadline",
        values.deadline
          ? dayjs(values.deadline).format("YYYY-MM-DD HH:mm:ss")
          : selectedTask?.deadline || ""
      );

      fd.append("steps_required", values.steps_required ? "true" : "false");

      // ✅ Biriktirilgan foydalanuvchilar — string qilib yuboramiz
      if (assignedKeys?.length) {
        fd.append("assigned_user_ids", assignedKeys.join(","));
      }

      // ✅ Steps — JSON qilib yuboramiz
      // if (values.steps_required && Array.isArray(values.steps)) {
      const normalizedSteps = values.steps
        .filter((s) => s?.title)
        .map((s, idx) => ({
          title: s.title,
          order: +s.order,
          is_completed: s.is_completed ?? false,
        }));


      fd.append("steps", JSON.stringify(normalizedSteps));
      if (imageFile) fd.append("image", imageFile);
      if (docFile) fd.append("file", docFile);

      await dispatch(editMyTask({ taskId: selectedTask.id, data: fd })).unwrap();

      message.success("Vazifa muvaffaqiyatli yangilandi.");
      dispatch(myTasks());
      closeEdit();
    } catch (e) {
      message.error(safeErrText(e) || "Yangilashda xatolik yuz berdi.");
    } finally {
      setEditSubmitting(false);
    }
  };


  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (task) => {
    if (!task?.id) return;
    try {
      setDeletingId(task.id);
      await dispatch(deleteMyTask({ taskId: task.id, data: {} })).unwrap();
      message.success("Vazifa o'chirildi.");
      dispatch(myTasks());
    } catch (e) {
      message.error(safeErrText(e) || "O'chirishda xatolik yuz berdi.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={tasks_styles.tasks_list}>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Yuborilgan topshiriqlar</Breadcrumb.Item>
      </Breadcrumb>

      <Card title={'Yuborilgan topshiriqlar'} >
        {filteredMyTasks.length > 0 ? (
          <Collapse accordion>
            {filteredMyTasks.map((item, index) => {
              const assignedUsers = item.assigned_users;

              return (
                <Panel
                  header={
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <strong>{item.title}</strong>
                        <div>
                          <Tag className="mt-1">
                            Muddati:
                          </Tag>
                          {item.deadline && (
                            <Text type="secondary" className="ms-2">
                              {dayjs(item.deadline).format("DD.MM.YYYY HH:mm")}
                            </Text>
                          )}
                        </div>
                      </div>
                      <Avatar.Group
                        max={{ count: 3, style: { color: "#f56a00", backgroundColor: "#fde3cf" } }}
                      >
                        {assignedUsers?.map((au, idx) => (
                          <Avatar
                            key={au.id ?? idx}
                            src={au?.picture || "https://via.placeholder.com/60"}
                          />
                        ))}
                      </Avatar.Group>
                    </div>
                  }
                  key={item.id ?? index}
                >
                  <Space wrap flex>
                    <Button
                      type="primary"
                      rap icon={<EditOutlined />}
                      size="middle"
                      onClick={() => openEdit(item)}
                    >
                      Tahrirlash
                    </Button>

                    <Popconfirm
                      title="Vazifani o‘chirishni tasdiqlaysizmi?"
                      description="Bu amalni orqaga qaytarib bo‘lmaydi."
                      okText="Ha, o‘chir"
                      cancelText="Bekor qilish"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(item)}
                      icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
                    >
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        size="middle"
                        loading={deletingId === item.id}
                      >
                        O‘chirish
                      </Button>
                    </Popconfirm>
                  </Space>

                  <p className="mt-3">
                    <strong>Izoh: </strong>
                    {item.description}
                  </p>

                  <div className={`table-responsive ${tasks_styles.table_wrapper}`}>
                    <table className={`table table-bordered ${styles.table}`}>
                      <thead className={`text-primary thead ${styles.thead}`}>
                        <tr>
                          <th className="text-center" colSpan={4}>
                            Biriktirilgan xodimlar
                          </th>
                        </tr>
                        <tr>
                          <td className={tasks_styles.number_column}>№</td>
                          <td>FIO</td>
                          <td>Status</td>
                          <td>Jarayon</td>
                        </tr>
                      </thead>
                      <tbody className={`tbody ${styles.tbody}`}>
                        {assignedUsers?.map((au, idx) => {
                          const i = idx + 1;
                          const st2 =
                            statusMap[au.task_status] || {
                              label: au.task_status,
                              className: "badge bg-dark",
                            };
                          let now =
                            au.steps_count > 0
                              ? (au.completed_steps_count / au.steps_count) * 100
                              : 0;
                          if (au.task_status === "pending") now = 0;

                          return (
                            <tr key={au.id ?? idx}>
                              <td>{i}</td>
                              <td>
                                <Avatar
                                  src={au?.picture || "https://via.placeholder.com/60"}
                                  size={30}
                                  style={{ marginRight: 8 }}
                                />
                                <Link to={`/tasks/my-detail/${au.task_id}`}>
                                  {au.first_name} {au.surname} {au.patronymic}
                                </Link>
                              </td>
                              <td className="text-center">
                                <span className={st2.className}>{st2.label}</span>
                              </td>
                              <td>
                                <Progress
                                  percent={Math.round(now)}
                                  size="small"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) :
          (
            <Empty description="Ma’lumot topilmadi" />
          )
        }
      </Card>

      <Modal
        open={editOpen}
        onCancel={closeEdit}
        title={
          <Space direction="vertical" size={0}>
            <Title level={4} className="mb-0">
              Vazifani tahrirlash
            </Title>
            <Text type="secondary">{selectedTask?.title}</Text>
          </Space>
        }
        okText="Saqlash"
        cancelText="Bekor qilish"
        onOk={() => form.submit()}
        confirmLoading={editSubmitting}
        maskClosable={true}
        destroyOnClose
        width={900}
      >
        <Form form={form} layout="vertical" onFinish={handleEditFinish} requiredMark="optional">
          <Form.Item name="is_approval_required" initialValue={true} hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label="Sarlavha"
            name="title"
            rules={[
              { required: true, message: "Sarlavha majburiy" },
              { max: 200, message: "200 ta belgidan oshmasin" },
            ]}
          >
            <Input placeholder="Masalan: Hisobot tayyorlash" />
          </Form.Item>

          <Form.Item label="Izoh" name="description" rules={[{ required: true, message: "Izoh majburiy" }]}>
            <TextArea placeholder="Vazifa haqida batafsil" rows={4} />
          </Form.Item>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label="Tasdiqlovchi"
                name="approver_id"
                rules={[{ required: true, message: "Tasdiqlovchini tanlang" }]}
              >
                <Select
                  placeholder="Tasdiqlovchini tanlang"
                  options={userOptions.map((u) => ({ value: u.id, label: u.label }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item label="Muddat (deadline)" name="deadline" tooltip='Format: "2025-09-29T04:06:43.474Z"'>
                <DatePicker showTime className="w-100" placeholder="Sana va vaqtni tanlang" format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </div>

            <div className="col-12">
              <Form.Item label="Biriktirilgan foydalanuvchi (ixtiyoriy)">
                <Transfer
                  dataSource={transferData}
                  targetKeys={assignedKeys}
                  onChange={setAssignedKeys}
                  showSearch
                  listStyle={{ width: "100%", height: 300 }}
                  titles={["Mavjud", "Biriktirilgan"]}
                  filterOption={(inputValue, item) =>
                    item.title.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  render={(item) => item.title}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item label="Qadamlar talab qilinadimi?" name="steps_required" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
          </div>

          <Divider className="my-3">Vazifalar</Divider>

          <Form.List name="steps">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} className="border rounded p-3 mb-2">
                    <Form.Item name={[name, "id"]} style={{ display: "none" }}>
                      <Input />
                    </Form.Item>
                    <div className="row">
                      <div className="col-md-10">
                        <Form.Item
                          {...rest}
                          name={[name, "title"]}
                          label="Vazifa nomi"
                          rules={[{ required: true, message: "Vazifa nomi majburiy" }]}
                        >
                          <Input placeholder="Masalan: Ma'lumotlarni yig‘ish" />
                        </Form.Item>
                      </div>

                      <div className="col-md-2 d-flex align-items-center justify-content-end">
                        <Button danger onClick={() => remove(name)} icon={<DeleteOutlined />}>
                          O‘chirish
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={() => add({ title: "", order: fields.length + 1 })} icon={<PlusOutlined />}>
                  Vazifa qo‘shish
                </Button>
              </>
            )}
          </Form.List>

          <Divider className="my-3">Fayllar</Divider>

          <div className="row">
            <div className="col-md-6">
              <Form.Item label="Rasm (yangi yuklasangiz eski o‘chadi)">
                <Upload
                  beforeUpload={beforeUpload}
                  maxCount={1}
                  onRemove={() => setImageFile(null)}
                  onChange={({ fileList }) => {
                    setImageFile(fileList.length ? fileList[0].originFileObj : null);
                  }}
                >
                  <Button icon={<UploadOutlined />}>Rasm yuklash</Button>
                </Upload>
                {selectedTask?.image && (
                  <div className="form-text mt-1">
                    Joriy rasm: <code>{String(selectedTask.image).split("/").pop()}</code>
                  </div>
                )}
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item label="Fayl (yangi yuklasangiz eski o‘chadi)">
                <Upload
                  beforeUpload={beforeUpload}
                  maxCount={1}
                  onRemove={() => setDocFile(null)}
                  onChange={({ fileList }) => {
                    setDocFile(fileList.length ? fileList[0].originFileObj : null);
                  }}
                >
                  <Button icon={<UploadOutlined />}>Fayl yuklash</Button>
                </Upload>
                {selectedTask?.file && (
                  <div className="form-text mt-1">
                    Joriy fayl: <code>{String(selectedTask.file).split("/").pop()}</code>
                  </div>
                )}
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default MyTasksList;
